import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { useDashboardSlice } from '../../slice';
import { useAnalyticsContext } from '../analytics-context';
import { toast } from 'sonner';

const GaIntegrationForm: React.FC = () => {
  const encodeBase64 = (value: string) => {
    if (typeof window === 'undefined') {
      return Buffer.from(value, 'utf8').toString('base64');
    }
    return window.btoa(unescape(encodeURIComponent(value)));
  };
  const {
    useGaConfigQuery,
    useUpdateGaConfigMutation,
    useTestGaConfigMutation,
  } = useDashboardSlice();

  const { refreshGaData, ga } = useAnalyticsContext();
  const { data, isFetching, refetch } = useGaConfigQuery();
  const [updateConfig, { isLoading: isSaving }] = useUpdateGaConfigMutation();
  const [testConfig, { isLoading: isTesting }] = useTestGaConfigMutation();

  const [propertyId, setPropertyId] = useState('');
  const [credentialBase64, setCredentialBase64] = useState<string | null>(null);
  const [credentialFile, setCredentialFile] = useState<string | null>(null);

  useEffect(() => {
    if (data?.data?.propertyId !== undefined) {
      setPropertyId(data.data.propertyId);
    }
  }, [data?.data?.propertyId]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      setCredentialBase64(null);
      setCredentialFile(null);
      return;
    }
    try {
      const text = await file.text();
      const encoded = encodeBase64(text);
      setCredentialBase64(encoded);
      setCredentialFile(file.name);
      toast.success('Credentials file loaded. Remember to save the changes.');
    } catch (error) {
      console.error('Failed to read credentials file:', error);
      toast.error('Failed to read the credentials file. Please try again.');
      setCredentialBase64(null);
      setCredentialFile(null);
    }
  };

  const handleSave = async () => {
    if (!propertyId.trim()) {
      toast.error('Property ID is required.');
      return;
    }
    try {
      await updateConfig({
        propertyId: propertyId.trim(),
        credentials: credentialBase64 || undefined,
      }).unwrap();
      toast.success('Google Analytics settings saved.');
      setCredentialBase64(null);
      setCredentialFile(null);
      await refetch();
      refreshGaData();
    } catch (error: any) {
      console.error('Failed to save GA config:', error);
      toast.error(
        error?.data?.message ||
          error?.message ||
          'Failed to save Google Analytics settings.',
      );
    }
  };

  const handleTest = async () => {
    if (!propertyId.trim() && !data?.data?.propertyId) {
      toast.error('Property ID is required to run the test.');
      return;
    }
    try {
      await testConfig({
        propertyId: propertyId.trim() || data?.data?.propertyId,
        credentials: credentialBase64 || undefined,
      }).unwrap();
      toast.success('Successfully connected to Google Analytics.');
    } catch (error: any) {
      console.error('GA connection test failed:', error);
      toast.error(
        error?.data?.message ||
          error?.message ||
          'Failed to connect to Google Analytics.',
      );
    }
  };

  const statusMessage = useMemo(() => {
    if (ga.integration.isConfigured) {
      return 'Connected';
    }
    if (ga.integration.message) {
      return ga.integration.message;
    }
    return 'Not configured';
  }, [ga.integration]);

  return (
    <Card className="border-none shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-lg font-semibold">
          Google Analytics Integration
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              ga.integration.isConfigured
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {ga.integration.isConfigured ? 'Connected' : 'Not Connected'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="ga-property-id">GA4 Property ID</Label>
            <Input
              id="ga-property-id"
              placeholder="123456789"
              value={propertyId}
              onChange={event => setPropertyId(event.target.value)}
              disabled={isFetching || isSaving}
            />
            <p className="text-xs text-muted-foreground">
              You can find the property ID inside Google Analytics under Admin →
              Property Settings.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ga-credentials">Service Account JSON</Label>
            <Input
              id="ga-credentials"
              type="file"
              accept="application/json"
              onChange={handleFileChange}
              disabled={isFetching || isSaving}
            />
            <p className="text-xs text-muted-foreground">
              Upload the JSON key file for a service account with viewer access
              to the GA4 property.
            </p>
            {credentialFile && (
              <p className="text-xs text-muted-foreground">
                Loaded file:{' '}
                <span className="font-medium">{credentialFile}</span>
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleSave} disabled={isSaving || isFetching}>
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={isTesting || isFetching}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
          {!ga.integration.isConfigured && (
            <p className="text-xs text-muted-foreground">{statusMessage}</p>
          )}
        </div>

        <div className="rounded-lg border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground">
            How to create a Google Analytics service account
          </p>
          <ol className="mt-2 space-y-1 list-decimal list-inside">
            <li>
              Open the Google Cloud console and create a project (or pick an
              existing one).
            </li>
            <li>Enable the “Google Analytics Data API” for that project.</li>
            <li>
              Under “IAM &amp; Admin → Service accounts”, create a service
              account and download the JSON key file.
            </li>
            <li>
              In Google Analytics (GA4), go to “Admin → Property → Property
              access management” and invite the service account email with at
              least Viewer access.
            </li>
            <li>
              Upload that JSON file here and paste the GA4 property ID (you’ll
              find it under “Admin → Property details”).
            </li>
          </ol>
          <p className="mt-2">
            Tip: keep the JSON safe. Anyone with this file can access the
            property’s analytics data via the API.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default GaIntegrationForm;
